import { AxiosProgressEvent } from 'axios';
import { useCallback, useRef, useState } from 'react';
import { IConfigApp } from '../../config';
import { UserFeedbackResource } from '../../resources';
import { UserFeedbackType } from '../../types';
import { GetResourceHeaderWithToken } from '../useResource';

export type UserFeedbackCommonProps = {
  userFeedbackResource: UserFeedbackResource;
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  config: IConfigApp;
};

export const useFeedbackCommon = ({
  userFeedbackResource,
  getResourceHeaderWithToken,
  config,
}: UserFeedbackCommonProps) => {
  const [userFeedbackId, setUserFeedbackId] = useState<number | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [isDeletingAttachment, setIsDeletingAttachment] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const feedbackPromiseRef = useRef<Promise<number> | null>(null);

  /**
   * Function to ensure that `createUserFeedbackIdIfNotExist` runs only once,
   * even if invoked multiple times concurrently.
   */
  const createUserFeedbackIdIfNotExist = useCallback(async (): Promise<number> => {
    // If the promise already exists, return it to prevent duplicate calls
    if (feedbackPromiseRef.current) {
      return feedbackPromiseRef.current;
    }

    feedbackPromiseRef.current = (async () => {
      if (userFeedbackId) {
        return userFeedbackId;
      }
      const response = await userFeedbackResource.createUserFeedback(
        {
          userFeedbackCreateRequest: {
            frontendVersion: config.buildVersion,
          },
        },
        getResourceHeaderWithToken(),
      );

      setUserFeedbackId(response.data.userFeedbackId);

      return response.data.userFeedbackId;
    })();

    const result = await feedbackPromiseRef.current;

    return result;
  }, [userFeedbackId, userFeedbackResource, config.buildVersion, getResourceHeaderWithToken]);

  const confirmUserFeedback = useCallback(
    async (userFeedbackType: UserFeedbackType, userFeedbackText?: string) => {
      try {
        setIsSubmitting(true);
        const feedbackId = await createUserFeedbackIdIfNotExist();

        await userFeedbackResource.confirmUserFeedback(
          {
            userFeedbackId: feedbackId,
            userFeedbackConfirmRequest: {
              userFeedbackType: userFeedbackType,
              userFeedbackText: userFeedbackText,
            },
          },
          getResourceHeaderWithToken(),
        );

        setIsSent(true);
      } catch (error) {
        throw new Error(`Failed to confirm user feedback: ${error}`);
      } finally {
        setIsSubmitting(false);
      }
    },
    [setIsSubmitting, setIsSent, createUserFeedbackIdIfNotExist, getResourceHeaderWithToken, userFeedbackResource],
  );

  const uploadAttachment = useCallback(
    async (file: File, onUploadProgress?: (event: AxiosProgressEvent) => void, abortController?: AbortController) => {
      try {
        setIsUploadingAttachment(true);
        const feedbackId = await createUserFeedbackIdIfNotExist();
        const res = await userFeedbackResource.uploadAttachment(
          {
            userFeedbackId: feedbackId,
            file,
          },
          getResourceHeaderWithToken(),
          onUploadProgress,
          abortController,
        );
        return res.data;
      } finally {
        setIsUploadingAttachment(false);
      }
    },
    [setIsUploadingAttachment, getResourceHeaderWithToken, userFeedbackResource, createUserFeedbackIdIfNotExist],
  );

  const deleteAttachment = useCallback(
    async (attachmentId: number) => {
      try {
        setIsDeletingAttachment(true);
        const feedbackId = await createUserFeedbackIdIfNotExist();
        return userFeedbackResource.deleteAttachment(
          {
            attachmentId,
            userFeedbackId: feedbackId,
          },
          getResourceHeaderWithToken(),
        );
      } finally {
        setIsDeletingAttachment(false);
      }
    },
    [setIsDeletingAttachment, getResourceHeaderWithToken, userFeedbackResource, createUserFeedbackIdIfNotExist],
  );

  const deleteUserFeedback = useCallback(async () => {
    try {
      if (userFeedbackId) {
        return userFeedbackResource.deleteUserFeedback(
          {
            userFeedbackId,
          },
          getResourceHeaderWithToken(),
        );
      }
      return null;
    } catch (err) {
      // we don't want to show error to the user
      return null;
    }
  }, [getResourceHeaderWithToken, userFeedbackResource, userFeedbackId]);

  return {
    confirmUserFeedback,
    uploadAttachment,
    deleteAttachment,
    deleteUserFeedback,
    isLoading: isSubmitting || isUploadingAttachment || isDeletingAttachment,
    isSent,
  };
};
